import { useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import SEO from "../components/SEO";
import Logo from "../components/Logo";
import { LogOut, RefreshCw, Inbox } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Admin() {
  const [token, setToken] = useState(() => localStorage.getItem("bct_admin_token") || "");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [items, setItems] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async (t) => {
    try {
      const r = await fetch(`${API}/admin/enquiries`, { headers: { Authorization: `Bearer ${t}` } });
      if (r.status === 401) { logout(); return; }
      setItems(await r.json());
    } catch { toast.error("Could not load enquiries"); }
  }, []);

  useEffect(() => { if (token) load(token); }, [token, load]);

  const login = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const r = await fetch(`${API}/auth/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, password }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(typeof d.detail === "string" ? d.detail : "Login failed");
      localStorage.setItem("bct_admin_token", d.access_token);
      setToken(d.access_token);
      toast.success("Welcome back");
    } catch (err) { toast.error(err.message); }
    setBusy(false);
  };

  const logout = () => {
    localStorage.removeItem("bct_admin_token");
    setToken("");
    setItems(null);
  };

  const inputCls = "w-full rounded-xl border border-slate-200 px-3.5 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-royal/40 focus:border-royal";

  return (
    <>
      <SEO title="Admin — Enquiries" description="Admin area" path="/admin" />
      <section className="max-w-5xl mx-auto px-6 py-14 min-h-[60vh]">
        {!token ? (
          <form onSubmit={login} data-testid="admin-login-form" className="max-w-sm mx-auto bg-white rounded-2xl border border-slate-200 shadow-lg p-8">
            <div className="flex justify-center"><Logo /></div>
            <h1 className="mt-5 text-center font-heading font-bold text-xl text-navy">Admin Login</h1>
            <div className="mt-6 space-y-4">
              <input type="email" className={inputCls} placeholder="Email" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="admin-email-input" required />
              <input type="password" className={inputCls} placeholder="Password" value={password} onChange={(e) => setPassword(e.target.value)} data-testid="admin-password-input" required />
              <button type="submit" disabled={busy} data-testid="admin-login-btn" className="w-full rounded-xl bg-royal text-white font-bold py-3 text-sm hover:bg-royal-700 transition-colors disabled:opacity-60">
                {busy ? "SIGNING IN…" : "SIGN IN"}
              </button>
            </div>
          </form>
        ) : (
          <div>
            <div className="flex items-center justify-between flex-wrap gap-3">
              <h1 className="font-heading text-2xl font-extrabold text-navy flex items-center gap-2"><Inbox size={22} className="text-royal" /> Enquiries</h1>
              <div className="flex gap-2">
                <button onClick={() => load(token)} data-testid="admin-refresh-btn" className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 px-4 py-2 text-xs font-bold text-navy hover:border-royal"><RefreshCw size={13} /> Refresh</button>
                <button onClick={logout} data-testid="admin-logout-btn" className="inline-flex items-center gap-1.5 rounded-lg bg-navy text-white px-4 py-2 text-xs font-bold"><LogOut size={13} /> Logout</button>
              </div>
            </div>
            {items === null ? (
              <p className="mt-10 text-center text-sm text-slate-400">Loading…</p>
            ) : items.length === 0 ? (
              <p className="mt-10 text-center text-sm text-slate-400" data-testid="admin-empty">No enquiries yet. New website and WhatsApp enquiries will appear here.</p>
            ) : (
              <div className="mt-6 space-y-3" data-testid="admin-enquiry-list">
                {items.map((e, i) => (
                  <article key={e.id || i} className="bg-white rounded-2xl border border-slate-200 p-5">
                    <div className="flex flex-wrap items-center gap-2 text-[10px] font-bold uppercase tracking-wider">
                      <span className="rounded-full bg-royal-50 text-royal px-2.5 py-1">{e.source || "website"}</span>
                      <span className="text-slate-400">{e.created_at ? new Date(e.created_at).toLocaleString("en-IN") : ""}</span>
                    </div>
                    {(e.name || e.phone) && <p className="mt-2 font-heading font-bold text-navy">{e.name} {e.phone && <span className="text-royal font-body text-sm">· {e.phone}</span>}</p>}
                    {(e.pickup || e.drop) && <p className="mt-1 text-sm text-slate-700"><strong>{e.pickup}</strong> → <strong>{e.drop}</strong>{e.date ? ` · ${e.date}` : ""}{e.time ? ` ${e.time}` : ""}{e.passengers ? ` · ${e.passengers} pax` : ""}{e.vehicle ? ` · ${e.vehicle}` : ""}</p>}
                    {e.message && <p className="mt-1 text-sm text-slate-600">{e.message}</p>}
                  </article>
                ))}
              </div>
            )}
          </div>
        )}
      </section>
    </>
  );
}
